import _ from 'lodash';
import {RouteService} from "../../../../services/routeService";
import {NotificationService} from "../../../../services/notificationService";
import {GeneralService} from "../../../../services/generalService";
import {DICTIONARY} from "../../../../dictionary";
import {Component, OnInit, ViewChild} from "@angular/core";
import {CollaborationProtectionService} from "../../../../services/collaborationProtectionService";

@Component({
	selector: 'app-collaboration-assets',
	templateUrl: './collaboration-assets.component.html'
})
export class CollaborationAssetsComponent implements OnInit {
	constructor(private rs:RouteService,
				private ns:NotificationService,
				private cps:CollaborationProtectionService,
				public gs:GeneralService) {
	}
	dic = DICTIONARY;

	@ViewChild('assetsTable') assetsTable: any;

	assets;
	loadingAssets;
	filterData;
	currentAssetInfoObj;

	linkScopeTooltips: Record<string, string> = {
		anonymous: 'Anyone with the link can access (no login required)',
		organization: 'All users in your company can access (must sign in)',
		users: 'Only users with existing access (must sign in)',
		external: 'At least one or more external user has an access to this file'
	};

	// State passed from overview page via router
	presetFilter: any = null;

	ngOnInit() {
		if (window.history.state?.data) {
			this.presetFilter = window.history.state.data;
		}

		this.getCollaborationAssets();

		this.initFilters();
	};

	getCollaborationAssets = () => {
		this.loadingAssets = true;
		this.rs.getCollaborationAssets().then((response) => {
			this.assets = response;
			this.assets.forEach((assetObj) => {
				this.parseAssetDetails(assetObj);

				this.cps.parseResourceName(assetObj.resource_id);
			});

			this.loadingAssets = false;

			// Apply preset filters from overview navigation (deferred to run after table init)
			if (this.presetFilter) {
				setTimeout(() => {
					this.applyPresetFilter(this.presetFilter);
					this.presetFilter = null;
				});
			}

		}, (err) => {
			this.loadingAssets = false;
		});
	}

	initFilters = () => {
		this.filterData = {
			filterType: this.dic.CONSTANTS.tableFilters.collaborationAssets,
			filters: {
				type: ["message", "file"],
				status: ["Quarantined", "Released"],
				category: ['DLP', 'Malicious', 'Rule'],
				permissions: ['anonymous', 'organization', 'users'],
			},
			initFiltersFunction: this.initFilters,
		};
	}

	parseAssetDetails = (assetObj) => {
		if (assetObj.is_quarantine) {
			assetObj.accessWeight = -1;
		}
		else {
			const scopeWeights = {anonymous: 1000000, organization: 100000, external: 10000, users: 1000};
			const scopeWeight = scopeWeights[assetObj.permissions?.link?.scope] || 0;
			const membersCount = assetObj.resource_id?.members?.length || 0;
			const computed = scopeWeight + membersCount;
			assetObj.accessWeight = computed > 0 ? computed : -2;
		}

		if (assetObj.sensitivity_info) {
			assetObj._topSensitiveTypes = (assetObj.sensitivity_info.sensitive_types || [])
				.filter(t => t.score)
				.sort((a, b) => (b.score || 0) - (a.score || 0))
				.slice(0, 3)
				.map(t => t.sensitive_type);
		}

		if (assetObj.malicious_info) {
			assetObj._maliciousSubCategory = (assetObj.malicious_info.sub_status || [])
				.map(s => s.message)
				.slice(0, 2);
		}
	}

	searchAssets = (searchTerm, activeFilters) => {
		this.assets.forEach(record => {
			// search
			if (searchTerm) {
				const isFound = searchTextExecute(record, searchTerm);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}

			// filter
			if (activeFilters) {
				// need to match all filter types
				let numFilterToMatch = Object.keys(activeFilters).length;

				if (activeFilters['category']?.length) {
					if (activeFilters['category'].includes(record.category)) {
						numFilterToMatch--;
					}
				}
				if (activeFilters['status']?.length) {
					if ((activeFilters['status'].includes("Quarantined") && record.is_quarantine) ||
						(activeFilters['status'].includes("Released") && !record.is_quarantine)) {
						numFilterToMatch--;
					}
				}
				if (activeFilters['type']?.length) {
					if (activeFilters['type'].includes(record.asset_type)) {
						numFilterToMatch--;
					}
				}
				if (activeFilters['permissions']?.length) {
					if (activeFilters['permissions'].includes(record.permissions?.link?.scope)) {
						numFilterToMatch--;
					}
				}
				if (activeFilters['source']) {
					const resourceTypes = this.sourceToResourceTypes[activeFilters['source']] || [];
					if (resourceTypes.includes(record.resource_id?.resource_type)) {
						numFilterToMatch--;
					}
				}

				if (numFilterToMatch) {
					record.hide = true;
					return;
				}
			}

			record.hide = false;
		});
	}


	showAssetActions = (assetObj) => {
		const actions = ['View info'];
		if (assetObj.asset_type === 'file') {
			if (assetObj.is_quarantine) {
				actions.push('Release');
			}
			else {
				if (assetObj.folder_id) {
					actions.push('Quarantine');
				}
			}
		}
		actions.push('Delete');

		return actions;
	}

	selectAssetAction = (assetObj, action) => {
		switch (action) {
			case 'View info':
				this.viewAssetDetails(assetObj);
				break;

			case 'Release':
				this.releaseAssetAction([assetObj]);
				break;

			case 'Quarantine':
				this.quarantineAssetAction([assetObj]);
				break;

			case 'Delete':
				this.deleteCollaborationAsset([assetObj]);
				break;

		}
	}

	selectBulkAction = (selectedAssets ,action) => {
		switch (action) {
			case 'Delete':
				this.deleteCollaborationAsset(selectedAssets);
				break;

			case 'Release':
				this.releaseAssetAction(selectedAssets);
				break;

			case 'Quarantine':
				this.quarantineAssetAction(selectedAssets);
				break;
		}
	};

	showAssetsBulkActions = () => {
		return [
			'Release',
			'Quarantine',
			this.dic.CONSTANTS.contactActions.delete
		];
	}

	viewAssetDetails = (assetObj) => {
		this.currentAssetInfoObj = assetObj;
	}

	releaseAssetAction = (assets) => {
		assets = assets.filter(itm => itm.is_quarantine);
		if (!assets.length) {
			this.ns.showInfoMessage('Selected assets are already released');
			return;
		}

		this.gs.showPopup({
			title: `Release ${assets.length > 1 ? 'Assets' : 'Asset'}`,
			subTitle: assets.length > 1 ? `Are you sure you want to release ${assets.length} assets` : `Are you sure you want to release this asset?`,
			body: ['Asset will be restored to its original folder'],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Release',
			doneCb: () => {
				assets.forEach((assetObj) => {
					this.rs.doAssetAction(assetObj._id, {action: 'release'}).then((response) => {
						assetObj.is_quarantine = false;
						this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
					}, (err) => {
					});
				});
			}
		});
	}

	quarantineAssetAction = (assets) => {
		assets = assets.filter(itm => !itm.is_quarantine);
		if (!assets.length) {
			this.ns.showInfoMessage('Selected assets are already quarantined');
			return;
		}
		this.gs.showPopup({
			title: `Quarantine ${assets.length > 1 ? 'Assets' : 'Asset'}`,
			subTitle: assets.length > 1 ? `Are you sure you want to quarantine ${assets.length} assets` : `Are you sure you want to quarantine this asset?`,
			body: ['Asset will be quarantined in a dedicated SharePoint site'],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Quarantine',
			doneCb: () => {
				assets.forEach((assetObj) => {
					this.rs.doAssetAction(assetObj._id, {action: 'quarantine'}).then((response) => {
						assetObj.is_quarantine = true;
						this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
					}, (err) => {
					});
				});
			}
		});
	}

	deleteCollaborationAsset = (assets) => {
		this.gs.showPopup({
			title: `Delete ${assets.length > 1 ? 'Assets' : 'an Asset'}`,
			subTitle: assets.length > 1 ? `Are you sure you want to delete ${assets.length} assets` : `Are you sure you want to delete this asset?`,
			body: ['Note: only the asset record will be deleted from this list, the actual file/message will not be deleted from the protected resource'],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Delete',
			doneCb: () => {
				assets.forEach((assetObj) => {
					this.rs.deleteAssetObj(assetObj._id).then((response) => {
						this.assets = this.assets.filter(m => m._id !== assetObj._id);
						this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
					}, (err) => {
					});
				});
			}
		});
	}

	sourceToResourceTypes: Record<string, string[]> = {
		'Teams': ['chat', 'channel', 'meeting', 'team'],
		'SharePoint': ['site'],
		'OneDrive': ['drive'],
	};

	applyPresetFilter = (preset) => {
		const activeFilters: any = {};
		if (preset.category?.length) {
			activeFilters.category = preset.category;
		}
		if (preset.type?.length) {
			activeFilters.type = preset.type;
		}
		if (preset.permissions?.length) {
			activeFilters.permissions = preset.permissions;
		}
		if (preset.source) {
			activeFilters.source = preset.source;
		}
		const searchTerm = preset.search || '';
		const hasFilters = Object.keys(activeFilters).length > 0;

		// Set the table's internal search text and active filters so the UI reflects the state
		if (this.assetsTable) {
			if (searchTerm) {
				this.assetsTable.search.text = searchTerm;
			}
			if (hasFilters) {
				this.assetsTable.activeFilters = activeFilters;
			}
		}

		this.searchAssets(searchTerm, hasFilters ? activeFilters : null);
	}

	sensitivityScoreColor(score: number) {
		if (score >= 5) return '#FF4136';
		if (score >= 4) return '#FF851B';
		if (score >= 1) return 'darkorange';
		return 'limegreen';
	}

	exportToCsv = (sortBy) => {
		if (!this.assets?.length) {
			this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
			return;
		}

		let csvString = "Detected,User,Resource,Source,Type,Category,Details,Members,Permissions\n";

		let sortedTable = _.filter(this.assets, asset => {return !asset.hide});
		if (sortBy) {
			sortedTable = this.gs.sortTable(sortedTable, sortBy);
		}

		sortedTable.forEach((asset) => {
			const detected = asset.created ? new Date(asset.created).toLocaleString() : '';
			const user = asset.user || '';
			const resource = asset.resource_id?.name || '';
			const source = asset.resource_id?.resource_type || '';
			const type = asset.asset_type || '';
			const category = asset.category || '';
			const details = asset._topSensitiveTypes?.join('; ') || asset._maliciousSubCategory?.join('; ') || '';
			const members = asset.resource_id?.members?.length || 0;
			const permissions = asset.permissions?.link?.scope || '';
			csvString += `"${detected}","${user}","${resource}","${source}","${type}","${category}","${details}","${members}","${permissions}"\n`;
		});

		this.gs.exportCsv(csvString, `collaboration-assets.csv`);
	};

}

function searchTextExecute (assetObj, searchTerm) {
	searchTerm = searchTerm.toLowerCase();

	return ((assetObj.resource_id?.name?.toLowerCase().indexOf(searchTerm) > -1) ||
		(assetObj.resource_id?.resource_type?.toLowerCase().indexOf(searchTerm) > -1) ||
		(assetObj.user?.toLowerCase().indexOf(searchTerm) > -1) ||
		(assetObj.category?.toLowerCase().indexOf(searchTerm) > -1) ||
		(assetObj.malicious_info?.domain?.toLowerCase().indexOf(searchTerm) > -1) ||
		(assetObj._topSensitiveTypes?.some(t => t.toLowerCase().indexOf(searchTerm) > -1)) ||
		(assetObj.malicious_info?.sub_status?.some(s => s.message?.toLowerCase().indexOf(searchTerm) > -1)));
}
